<script setup lang="ts" generic="T">
import { computed, type Component } from 'vue';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import FilterOptionDisplay from '@/components/FilterBuilder/FilterOptionDisplay.vue';

/**
 * Single-select dropdown for enum-backed filter rules (severity, issue_type,
 * original_status). Mirrors {@link FilterMultiCombobox} so the value control
 * shape is consistent across "is"/"is not" (this component) and
 * "is one of"/"is none of" (the multi variant).
 *
 * `optionComponent` is the bridge to the shared badge components: pass
 * {@link SeverityBadge} for severity and the option is rendered both in the
 * dropdown list and as the trigger pill with the same colour scheme used in
 * the table cell and inspector.
 */
const props = withDefaults(
  defineProps<{
    options: T[];
    getValue: (option: T) => string;
    getLabel: (option: T) => string;
    optionComponent?: Component;
    placeholder?: string;
    disabled?: boolean;
    testIdPrefix?: string;
  }>(),
  {
    placeholder: 'Select...',
    disabled: false,
    testIdPrefix: 'filter-enum-select',
    optionComponent: undefined,
  },
);

const modelValue = defineModel<string | null>();

const selectedOption = computed(() =>
  props.options.find((option) => props.getValue(option) === modelValue.value) ?? null,
);

const selectedLabel = computed(() =>
  selectedOption.value ? props.getLabel(selectedOption.value) : '',
);
</script>

<template>
  <Select v-model="modelValue">
    <SelectTrigger
      :data-testid="`${testIdPrefix}-trigger`"
      :disabled="disabled"
      size="sm"
      class="w-full text-xs"
    >
      <FilterOptionDisplay
        v-if="optionComponent && selectedOption"
        :component="optionComponent"
        :value="getValue(selectedOption)"
        :label="selectedLabel"
        :option="selectedOption"
      />
      <SelectValue v-else :placeholder="placeholder" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem
        v-for="option in options"
        :key="getValue(option)"
        :value="getValue(option)"
        :data-testid="`${testIdPrefix}-option-${getValue(option)}`"
      >
        <FilterOptionDisplay
          v-if="optionComponent"
          :component="optionComponent"
          :value="getValue(option)"
          :label="getLabel(option)"
          :option="option"
        />
        <span v-else>{{ getLabel(option) }}</span>
      </SelectItem>
    </SelectContent>
  </Select>
</template>
